import { Container, FillGradient, Graphics, Text } from 'pixi.js';
import { COLOR, textStyle } from './theme.js';
import { artSprite } from './textures.js';

/** Элементы интерфейса макетов: панели, кнопки, бейджи, пузыри реплик. */

export function vertical(top: number, bottom: number): FillGradient {
  return new FillGradient({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: 1 },
    colorStops: [
      { offset: 0, color: top },
      { offset: 1, color: bottom },
    ],
    textureSpace: 'local',
  });
}

export interface PanelOptions {
  readonly width: number;
  readonly height: number;
  readonly radius?: number;
  readonly top?: number;
  readonly bottom?: number;
  readonly border?: number;
  readonly borderWidth?: number;
  readonly alpha?: number;
}

/** Панель макета: вертикальный градиент, обводка и светлая кромка сверху. */
export function panel(options: PanelOptions): Graphics {
  const { width, height } = options;
  const radius = options.radius ?? 18;
  const g = new Graphics()
    .roundRect(0, 0, width, height, radius)
    .fill({
      fill: vertical(options.top ?? COLOR.panelTop, options.bottom ?? COLOR.panelBottom),
      alpha: options.alpha ?? 0.95,
    })
    .stroke({ width: options.borderWidth ?? 2, color: options.border ?? COLOR.border });
  g.moveTo(radius, 2)
    .lineTo(width - radius, 2)
    .stroke({ width: 1, color: COLOR.white, alpha: 0.16 });
  return g;
}

export type ButtonVariant = 'primary' | 'secondary' | 'gold' | 'pink';

interface VariantLook {
  readonly top: number;
  readonly bottom: number;
  readonly border: number;
  readonly borderWidth: number;
  /** Тень-«подставка» цвета ink под кнопкой, px. */
  readonly lift: number;
  readonly text: number;
}

const LOOKS: Readonly<Record<ButtonVariant, VariantLook>> = {
  primary: {
    top: 0x7ff5e6,
    bottom: 0x2fbfae,
    border: COLOR.ink,
    borderWidth: 3,
    lift: 5,
    text: COLOR.ink,
  },
  pink: {
    top: 0xff8fb0,
    bottom: 0xd94b8c,
    border: COLOR.ink,
    borderWidth: 3,
    lift: 5,
    text: COLOR.white,
  },
  secondary: {
    top: COLOR.panelTop,
    bottom: COLOR.panelBottom,
    border: COLOR.border,
    borderWidth: 2,
    lift: 0,
    text: COLOR.text,
  },
  gold: {
    top: COLOR.goldTop,
    bottom: COLOR.goldBottom,
    border: COLOR.goldBorder,
    borderWidth: 2,
    lift: 0,
    text: COLOR.goldSoft,
  },
};

export interface ButtonOptions {
  readonly width: number;
  readonly height: number;
  readonly label: string;
  readonly variant?: ButtonVariant;
  /** Вторая строка под заголовком. */
  readonly sub?: string;
  /** Значок слева: ключ рисунка и цвет плитки под ним (без плитки — просто значок). */
  readonly icon?: { readonly key: string; readonly tile?: number; readonly size?: number };
  /** Значок справа (например, замок). */
  readonly trailing?: string;
  readonly radius?: number;
  readonly fontSize?: number;
  /** Заголовок по центру (без значка и подписи). */
  readonly center?: boolean;
  readonly disabled?: boolean;
  readonly onTap: () => void;
}

/** Кнопка макета: мышь, касание, наведение, нажатие; доступна с клавиатуры через Tab. */
export class UiButton extends Container {
  private readonly face = new Container();
  private readonly title: Text;
  private subtitle: Text | null = null;

  constructor(private readonly options: ButtonOptions) {
    super();
    const look = LOOKS[options.variant ?? 'secondary'];
    const { width, height } = options;
    const radius = options.radius ?? 16;
    if (look.lift > 0) {
      this.addChild(new Graphics().roundRect(0, look.lift, width, height, radius).fill(COLOR.ink));
    }
    const bg = new Graphics()
      .roundRect(0, 0, width, height, radius)
      .fill(vertical(look.top, look.bottom))
      .stroke({ width: look.borderWidth, color: look.border });
    if (look.lift === 0) {
      bg.moveTo(radius, 1.5)
        .lineTo(width - radius, 1.5)
        .stroke({ width: 1, color: COLOR.white, alpha: 0.16 });
    }
    this.face.addChild(bg);

    this.title = new Text({
      text: options.label,
      style: textStyle({
        size: options.fontSize ?? (options.sub ? 18 : 17),
        display: true,
        weight: '900',
        color: look.text,
      }),
    });
    const icon = options.icon;
    const iconSize = icon?.size ?? 24;
    const glyph = icon ? artSprite(icon.key, iconSize) : null;

    if (options.center) {
      // Значок и заголовок вместе по центру кнопки.
      const gap = glyph ? iconSize + 10 : 0;
      const left = width / 2 - (gap + this.title.width) / 2;
      if (glyph) {
        glyph.position.set(left + iconSize / 2, height / 2);
        this.face.addChild(glyph);
      }
      this.title.anchor.set(0, 0.5);
      this.title.position.set(left + gap, height / 2);
      this.face.addChild(this.title);
    } else {
      let x = 20;
      if (icon && glyph) {
        if (icon.tile !== undefined) {
          const tile = Math.min(height - 18, 52);
          this.face.addChild(
            new Graphics()
              .roundRect(12, height / 2 - tile / 2, tile, tile, 12)
              .fill(icon.tile)
              .stroke({ width: 2, color: COLOR.ink }),
          );
          glyph.position.set(12 + tile / 2, height / 2);
          x = 12 + tile + 14;
        } else {
          glyph.position.set(12 + iconSize / 2, height / 2);
          x = 12 + iconSize + 10;
        }
        this.face.addChild(glyph);
      }
      this.title.anchor.set(0, options.sub ? 1 : 0.5);
      this.title.position.set(x, options.sub ? height / 2 + 2 : height / 2);
      this.face.addChild(this.title);
      if (options.sub) {
        this.subtitle = new Text({
          text: options.sub,
          style: textStyle({
            size: 14,
            color: look.text === COLOR.ink ? COLOR.ink : COLOR.textSoft,
            weight: '500',
          }),
        });
        this.subtitle.position.set(x, height / 2 + 5);
        this.face.addChild(this.subtitle);
      }
    }
    if (options.trailing) {
      const mark = artSprite(options.trailing, 20);
      mark.position.set(width - 24, height / 2);
      this.face.addChild(mark);
    }
    this.addChild(this.face);

    this.accessible = true;
    this.accessibleType = 'button';
    this.accessibleTitle = options.sub ? `${options.label}. ${options.sub}` : options.label;
    this.setDisabled(options.disabled ?? false);
    this.on('pointerover', () => (this.face.alpha = this.disabled ? 0.5 : 0.88));
    this.on('pointerout', () => {
      this.face.alpha = this.disabled ? 0.5 : 1;
      this.face.y = 0;
    });
    this.on('pointerdown', () => {
      if (!this.disabled) this.face.y = look.lift > 0 ? 3 : 1;
    });
    this.on('pointerup', () => (this.face.y = 0));
    this.on('pointertap', () => {
      if (!this.disabled) this.options.onTap();
    });
  }

  private disabled = false;

  setDisabled(disabled: boolean): void {
    this.disabled = disabled;
    this.eventMode = 'static';
    this.cursor = disabled ? 'not-allowed' : 'pointer';
    this.face.alpha = disabled ? 0.5 : 1;
  }

  setSub(text: string): void {
    if (this.subtitle) this.subtitle.text = text;
  }
}

/** Бейдж состояния соединения (SPEC.md, 17.4): «В сети» или предупреждение. */
export class NetChip extends Container {
  private readonly bg = new Graphics();
  private readonly dot = new Graphics();
  private readonly caption = new Text({ text: '', style: textStyle({ size: 14, weight: '500' }) });

  constructor() {
    super();
    this.caption.anchor.set(0, 0.5);
    this.caption.position.set(34, 22);
    this.addChild(this.bg, this.dot, this.caption);
    this.set('online');
  }

  set(state: 'online' | 'offline' | 'server-down'): void {
    const online = state === 'online';
    this.caption.text = online
      ? 'В сети'
      : state === 'offline'
        ? 'Нет интернета'
        : 'Сервер недоступен';
    this.caption.style.fill = online ? COLOR.textSoft : COLOR.goldSoft;
    const width = 34 + this.caption.width + 16;
    this.bg
      .clear()
      .roundRect(0, 0, width, 44, 12)
      .fill({ color: online ? 0x0e1c3a : COLOR.gold, alpha: online ? 0.6 : 0.1 })
      .stroke({ width: 2, color: online ? COLOR.borderDim : COLOR.gold, alpha: online ? 1 : 0.45 });
    this.dot.clear().circle(19, 22, 5);
    if (online) this.dot.fill(COLOR.teal);
    else this.dot.stroke({ width: 2, color: COLOR.gold });
  }
}

/** Пузырь реплики персонажа: хвостик справа (главный экран) или снизу слева (бой). */
export function speechBubble(
  speaker: string,
  line: string,
  maxWidth = 210,
  tail: 'right' | 'bottom' = 'right',
): Container {
  const box = new Container();
  const name = new Text({
    text: speaker.toUpperCase(),
    style: textStyle({
      size: 10,
      display: true,
      weight: '800',
      color: COLOR.rose,
      letterSpacing: 1,
    }),
  });
  const text = new Text({
    text: line,
    style: textStyle({
      size: 16,
      weight: '700',
      color: COLOR.ink,
      wrap: maxWidth - 32,
      lineHeight: 21,
    }),
  });
  name.position.set(16, 10);
  text.position.set(16, 26);
  const width = Math.max(name.width, text.width) + 32;
  const height = 26 + text.height + 12;
  const right = (g: Graphics, color: number, size: number, dy: number) =>
    g.poly([width - 3, 30 + dy, width - 3 + size, 41, width - 3, 52 - dy]).fill(color);
  const bottom = (g: Graphics, color: number, size: number, dx: number) =>
    g.poly([29 + dx, height - 3, 40, height - 3 + size, 51 - dx, height - 3]).fill(color);
  const drawTail = tail === 'right' ? right : bottom;
  const bg = new Graphics().roundRect(0, 4, width, height, 18).fill(COLOR.ink);
  bg.roundRect(0, 0, width, height, 18).fill(COLOR.white).stroke({ width: 3, color: COLOR.ink });
  drawTail(bg, COLOR.ink, 19, 0);
  drawTail(bg, COLOR.white, 12, 4);
  box.addChild(bg, name, text);
  return box;
}

/** Клавиша-подсказка (`kbd` макета): тёмная плашка с подставкой. */
export function keyCap(caption: string): Container {
  const box = new Container();
  const t = new Text({
    text: caption,
    style: textStyle({ size: 11, display: true, weight: '800' }),
  });
  const width = Math.max(30, t.width + 18);
  box.addChild(
    new Graphics()
      .roundRect(0, 3, width, 30, 8)
      .fill(COLOR.ink)
      .roundRect(0, 0, width, 30, 8)
      .fill(vertical(0x2a527a, 0x16304f))
      .stroke({ width: 2, color: COLOR.ink }),
  );
  t.anchor.set(0.5);
  t.position.set(width / 2, 15);
  box.addChild(t);
  return box;
}

/** Переключатель настройки: подпись слева, флажок справа; доступен с клавиатуры. */
export class UiToggle extends Container {
  private readonly box = new Graphics();

  constructor(
    private readonly caption: string,
    private value: boolean,
    width: number,
    private readonly onChange: (value: boolean) => void,
  ) {
    super();
    const t = new Text({ text: caption, style: textStyle({ size: 15 }) });
    t.anchor.set(0, 0.5);
    t.position.set(0, 22);
    this.box.position.set(width - 24, 10);
    this.addChild(new Graphics().rect(0, 0, width, 44).fill({ color: COLOR.ink, alpha: 0.001 }));
    this.addChild(t, this.box);
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.accessible = true;
    this.accessibleType = 'button';
    this.on('pointertap', () => this.set(!this.value));
    this.draw();
  }

  set(value: boolean): void {
    this.value = value;
    this.draw();
    this.onChange(value);
  }

  private draw(): void {
    this.box.clear().roundRect(0, 0, 24, 24, 6);
    if (this.value) {
      this.box.fill(COLOR.mint).stroke({ width: 2, color: COLOR.ink });
      this.box
        .moveTo(5, 12.5)
        .lineTo(10, 17.5)
        .lineTo(19, 7)
        .stroke({ width: 3, color: COLOR.ink, cap: 'round', join: 'round' });
    } else {
      this.box.fill({ color: COLOR.ink, alpha: 0.5 }).stroke({ width: 2, color: COLOR.border });
    }
    this.accessibleTitle = `${this.caption}: ${this.value ? 'включено' : 'выключено'}`;
  }
}

/**
 * Ползунок громкости 0–100 %: перетаскивание или нажатие по дорожке.
 * С клавиатуры (Tab, Enter) значение растёт шагом 10 % и после 100 % начинается с нуля.
 */
export class UiSlider extends Container {
  private readonly value = new Text({
    text: '',
    style: textStyle({ size: 13, display: true, weight: '800', color: COLOR.textSoft }),
  });
  private readonly track = new Graphics();
  private dragging = false;
  private readonly trackWidth: number;

  constructor(
    private readonly caption: string,
    private percent: number,
    width: number,
    private readonly onChange: (percent: number) => void,
  ) {
    super();
    this.trackWidth = width;
    const t = new Text({ text: caption, style: textStyle({ size: 15 }) });
    this.value.anchor.set(1, 0);
    this.value.position.set(width, 2);
    this.track.position.set(0, 28);
    this.addChild(t, this.value, this.track);
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.hitArea = {
      contains: (x: number, y: number) => x >= -6 && x <= width + 6 && y >= 0 && y <= 52,
    };
    this.accessible = true;
    this.accessibleType = 'button';
    this.on('pointerdown', (e) => {
      this.dragging = true;
      this.setFrom(e.global.x);
    });
    this.on('globalpointermove', (e) => {
      if (this.dragging) this.setFrom(e.global.x);
    });
    const stop = () => (this.dragging = false);
    this.on('pointerup', stop);
    this.on('pointerupoutside', stop);
    // Клавиатура: система доступности Pixi присылает простое событие, без указателя.
    this.on('click', (e: { pointerType?: string }) => {
      if (e.pointerType === undefined) this.set((this.percent + 10) % 110);
    });
    this.draw();
  }

  private setFrom(globalX: number): void {
    const local = this.toLocal({ x: globalX, y: 0 }).x;
    this.set(Math.round(Math.max(0, Math.min(1, local / this.trackWidth)) * 100));
  }

  set(percent: number): void {
    const next = Math.max(0, Math.min(100, Math.round(percent)));
    if (next === this.percent) return;
    this.percent = next;
    this.draw();
    this.onChange(next);
  }

  private draw(): void {
    const w = this.trackWidth;
    const x = (w * this.percent) / 100;
    this.track
      .clear()
      .roundRect(0, 8, w, 8, 4)
      .fill({ color: COLOR.ink, alpha: 0.6 })
      .roundRect(0, 8, x, 8, 4)
      .fill(COLOR.mint)
      .circle(x, 12, 10)
      .fill(COLOR.mint)
      .stroke({ width: 3, color: COLOR.ink });
    this.value.text = `${this.percent}%`;
    this.accessibleTitle = `${this.caption}: ${this.percent}%`;
  }
}

/** Строка мелкого текста. */
export function label(text: string, size: number, color: number = COLOR.text): Text {
  return new Text({ text, style: textStyle({ size, color }) });
}
